import { api } from '@/api';
import { AppBudget } from '@polybank/shared';

export type BudgetStatus = 'on_track' | 'near_limit' | 'over_budget';

export interface BudgetWithCalculations extends AppBudget {
  spentAmount: number;
  remainingAmount: number;
  progressPercentage: number;
  status: BudgetStatus;
}

export interface BudgetsApiResponse {
  success: boolean;
  data: BudgetWithCalculations[];
  cached?: boolean;
}

export interface BudgetApiResponse {
  success: boolean;
  data?: BudgetWithCalculations;
  error?: string;
  cached?: boolean;
}

/**
 * Service for budget API operations with backend calculations
 */
class BudgetsApiService {
  async getAllBudgetsWithCalculations(): Promise<BudgetWithCalculations[]> {
    const result = await api.v1.budgets.get();
    if (result.error) throw result.error;
    const response = result.data;
    if (!response.success) {
      throw new Error('Failed to fetch budgets');
    }
    return response.data;
  }
  /**
   * Get a single budget with calculated spent amount from the backend
   */
  async getBudgetWithCalculations(
    budgetId: string
  ): Promise<BudgetWithCalculations | null> {
    const result = await api.v1.budgets({ id: budgetId }).get();
    if (result.error) throw result.error;
    const response = result.data as BudgetApiResponse;
    if (!response.success) {
      return null;
    }
    return response.data || null;
  }

  async createBudget(data: {
    categories: string[];
    amount: number;
    period: string;
    start_date: string;
    end_date: string;
    currency: string;
  }) {
    const result = await api.v1.budgets.post(data);
    if (result.error) throw result.error;
    return result.data;
  }

  async updateBudget(
    budgetId: string,
    updates: Partial<{
      categories: string[];
      amount: number;
      period: string;
      start_date: string;
      end_date: string;
      currency: string;
    }>
  ) {
    const result = await api.v1.budgets({ id: budgetId }).put(updates);
    if (result.error) throw result.error;
    return result.data;
  }

  async deleteBudget(budgetId: string) {
    const result = await api.v1.budgets({ id: budgetId }).delete();
    if (result.error) throw result.error;
    return result.data;
  }
}

export const budgetsApiService = new BudgetsApiService();
